import React from 'react';
import { X, Pencil } from '@phosphor-icons/react';
import classNames from 'classnames';

import { Spinner, Tooltip } from '../general';

interface Props {
  type: 'delete' | 'edit' | 'custom';
  icon?: React.ReactNode;
  variant?: string;
  position?: 'left' | 'right';
  tooltipText?: string;
  deleteType?: 'clip' | 'person' | 'photo';
  isProcessing?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  hidePopover?: boolean;
}

const SideIcon: React.FC<Props> = (props) => {
  const {
    type,
    icon,
    variant,
    position,
    tooltipText,
    deleteType,
    isProcessing,
    onClick,
    hidePopover,
  } = props;

  const positionClass = classNames({
    'absolute z-[2]': true,
    'top-[1.25rem] right-[1.25rem]':
      type === 'delete' && deleteType === 'person',
    'top-[-0.25rem] right-[-0.25rem]':
      type === 'delete' && deleteType === 'photo',
    'top-[-0.55rem] right-[-0.55rem]':
      (type === 'delete' && deleteType !== 'person') || position === 'right',
    'top-[-0.55rem] left-[-0.55rem]': type === 'edit' || position === 'left',
  });

  const iconClass = classNames({
    'w-6 h-6 items-center justify-center rounded-full ring-2 ring-white cursor-pointer transition-all duration-300': true,
    'text-white hidden group-hover:flex group-focus:flex hover:scale-110':
      type === 'edit' || type === 'delete',
    'bg-success-900': type === 'edit',
    'bg-error-900': type === 'delete',
    'flex bg-white text-black border border-black border-solid':
      type === 'custom' && (!variant || variant === 'black'),
  });

  // delete icon – shows X or spinner if deleting, also has popover of hover
  if (type === 'delete')
    return (
      <Tooltip
        text={
          <div className="w-72">
            Clicking 'X' will <b>delete</b> the {deleteType} and this action{' '}
            <b>cannot be undone</b>
          </div>
        }
        hide={!deleteType || hidePopover}
        className={positionClass}
      >
        <div id="delete-side-icon" className={iconClass} onClick={onClick}>
          {isProcessing ? (
            <Spinner size="2xs" color="white" />
          ) : (
            <X weight="bold" className="text-sm" />
          )}
        </div>
      </Tooltip>
    );

  // edit icon – shows pen
  if (type === 'edit')
    return (
      <div className={classNames(positionClass, iconClass)} onClick={onClick}>
        <Pencil weight="fill" className="text-sm" />
      </div>
    );

  // custom icon
  if (type === 'custom')
    return (
      <Tooltip
        text={<div className="w-72">{tooltipText}</div>}
        className={positionClass}
        hide={hidePopover}
      >
        <div className={iconClass} onClick={onClick}>
          {icon}
        </div>
      </Tooltip>
    );

  return null;
};

export default SideIcon;
